import { mergeStyles, Stack } from "@fluentui/react";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlineIcon from "@mui/icons-material/InfoOutlined";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  getGridNumericOperators,
  getGridStringOperators,
  GridColDef,
  GridFilterModel,
  GridSortModel
} from "@mui/x-data-grid";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { renderHeaderWithTooltip } from "../components/PartnerSearch/PartnerSearch.common";
import {
  CCP_PARTNER_PROGRAM_URL,
  COPY_TITLE,
  PARTNER_DATA_GRID_CONFIG,
  tooltipTexts
} from "../components/PartnerSearch/PartnerSearch.constants";
import { IPartnerData } from "../interfaces/PartnerView.interfaces";
import { getPartners } from "../Services/AzSearchServices";
import { callSearchAPIs, copy, getPartnerSearchInput, formatCountWithCommas } from "../Utils/js-common";
import { CustomDataGrid } from "./CustomDataGrid/CustomDataGrid";
import { CustomLinkNavigate } from "./CustomLinkNavigate";
import { IRows } from "./Interfaces/CustomDataGrid.interfaces";
import { CUSTOM_PARTNER_LIST_STYLES } from "./Styles/CustomPartnerList.style";

export function CustomPartnerList({
  initialFilters,
  searchText,
  skipColumnsFilterable
}: Readonly<{
  initialFilters: string;
  searchText: any;
  skipColumnsFilterable?: string[];
}>) {
  const [title, setTitle] = useState(COPY_TITLE);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [rows, setRows] = useState<IRows>(PARTNER_DATA_GRID_CONFIG.rows);
  const [paginationOptions, setPaginationOptions] = useState(PARTNER_DATA_GRID_CONFIG.pagination);
  const [filterOptions, setFilterOptions] = useState<GridFilterModel>({
    items: []
  });
  const [sortOptions, setSortOptions] = useState<GridSortModel>([]);

  const columns: GridColDef[] = [
    {
      headerName: "PartnerID",
      field: "mpnId",
      width: 130,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.partnerID),
      renderCell: ({ row }: { row: IPartnerData }) => (
        <Stack horizontal styles={{ root: CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idContainer }}>
          <Tooltip title={title} leaveDelay={500} arrow>
            <IconButton
              size="small"
              onClick={() => copyValue(row.mpnId, setTitle)}
              onMouseEnter={() => onMouseEnterTenantId(setTitle)}
              className={mergeStyles(CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idTooltipContainerBtn)}
            >
              <ContentCopyIcon sx={CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idTooltipCopyIconBtn} />
            </IconButton>
          </Tooltip>
          <CustomLinkNavigate to={navigateToMPNPage(row.partnerOneID, row.mpnId)} text={row.mpnId} />
        </Stack>
      )
    },
    {
      headerName: "PartnerName",
      field: "mpnName",
      width: 200,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.partnerName),
      renderCell: ({ row }: any) => (
        <CustomLinkNavigate to={navigateToMPNPage(row.partnerOneID, row.mpnId)} text={row.mpnName} />
      )
    },
    {
      headerName: "PartnerOneID",
      field: "partnerOneID",
      width: 152,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.partnerOneId),
      renderCell: ({ row }: { row: IPartnerData }) => (
        <Stack horizontal styles={{ root: CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idContainer }}>
          <Tooltip title={title} leaveDelay={500} arrow>
            <IconButton
              size="small"
              onClick={() => copyValue(row.partnerOneID, setTitle)}
              onMouseEnter={() => onMouseEnterTenantId(setTitle)}
              className={mergeStyles(CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idTooltipContainerBtn)}
            >
              <ContentCopyIcon sx={CUSTOM_PARTNER_LIST_STYLES.customDataGridColumns.idTooltipCopyIconBtn} />
            </IconButton>
          </Tooltip>
          <CustomLinkNavigate to={navigateTo(row.partnerOneID)} text={row.partnerOneID} />
        </Stack>
      )
    },
    {
      headerName: "PartnerOneName",
      field: "partnerOneName",
      width: 200,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.partnerOneName),
      renderCell: ({ row }: any) => <CustomLinkNavigate to={navigateTo(row.partnerOneID)} text={row.partnerOneName} />
    },
    {
      headerName: "IsElite",
      field: "isElite",
      width: 110,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {params.colDef.headerName}
          <Tooltip
            title={
              <span>
                Partner is part of the Security Elite Partner Program. More info at{" "}
                <a
                  href={CCP_PARTNER_PROGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "white", textDecoration: "underline", fontWeight: "bold" }}
                >
                  CCP Partner Program
                </a>
              </span>
            }
            style={{ alignItems: "center", marginBottom: 1 }}
          >
            <InfoOutlineIcon
              aria-label="Info"
              sx={{
                fontSize: "1.3rem"
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </span>
      ),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {row.isElite === 0 ? (
            <></>
          ) : (
            <CheckIcon
              titleAccess="true"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "IsChannelPartner",
      field: "isChannelPartner",
      width: 180,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {params.colDef.headerName}
          <Tooltip
            title={
              <span>
                Partner is part of the CCP Channel Partner Initiative. More info at{" "}
                <a
                  href={CCP_PARTNER_PROGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "white", textDecoration: "underline", fontWeight: "bold" }}
                >
                  CCP Partner Program
                </a>
              </span>
            }
            style={{ alignItems: "center", marginBottom: 1 }}
          >
            <InfoOutlineIcon
              aria-label="Info"
              sx={{
                fontSize: "1.3rem"
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </span>
      ),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {row.isChannelPartner === 0 ? (
            <></>
          ) : (
            <CheckIcon
              titleAccess="true"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "IsDefault",
      field: "isDefault",
      width: 125,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.isDefault),
      renderCell: ({ row }: any) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          {row.isDefault === 0 ? (
            <></>
          ) : (
            <CheckIcon
              titleAccess="true"
              sx={{
                color: "green",
                fontSize: "1.3rem"
              }}
            />
          )}
        </div>
      )
    },
    {
      headerName: "Partner Country",
      field: "partnerCountry",
      width: 170,
      sortable: true,
      hideSortIcons: true,
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.partnerCountry)
    },
    {
      headerName: "CPOR Tenants Count",
      field: "cporCustomersCount",
      width: 200,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.cporTenantsCount),
      renderCell: ({ row }: any) => <>{formatCountWithCommas(row.cporCustomersCount)}</>
    },
    {
      headerName: "CSP Tenants Count",
      field: "cspCustomersCount",
      width: 188,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.cspTenantsCount),
      renderCell: ({ row }: any) => <>{formatCountWithCommas(row.cspCustomersCount)}</>
    },
    {
      headerName: "PAL Tenants Count",
      field: "palCustomersCount",
      width: 188,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.palTenantsCount),
      renderCell: ({ row }: any) => <>{formatCountWithCommas(row.palCustomersCount)}</>
    },
    {
      headerName: "CPOR Partner Reach",
      field: "cporLoginMAU",
      width: 200,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.cporPartnerReach),
      renderCell: ({ row }: any) => <>{formatCountWithCommas(row.cporLoginMAU)}</>
    },
    {
      headerName: "CSP Partner Reach",
      field: "cspLoginMAU",
      width: 188,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.cspPartnerReach),
      renderCell: ({ row }: any) => <>{formatCountWithCommas(row.cspLoginMAU)}</>
    },
    {
      headerName: "PAL Partner Reach",
      field: "palLoginMAU",
      minWidth: 188,
      sortable: true,
      hideSortIcons: true,
      type: "number",
      renderHeader: (params) => renderHeaderWithTooltip(params.colDef.headerName, tooltipTexts.palPartnerReach),
      renderCell: ({ row }: any) => <>{formatCountWithCommas(row.palLoginMAU)}</>
    }
  ];

  useEffect(() => {
    const searchInput = getPartnerSearchInput(
      initialFilters,
      searchText,
      filterOptions,
      sortOptions,
      paginationOptions
    );
    callSearchAPIs(getPartners({ ...searchInput }), setLoading, setRows, setIsError);
  }, [initialFilters, paginationOptions, filterOptions, searchText, sortOptions]);

  return (
    <Box
      sx={{
        width: "calc(100% - 10px)",
        height: "calc(100% - 10px)",
        overflow: "auto"
      }}
    >
      <CustomDataGrid
        columns={columns.map((column) => getColumn(column, skipColumnsFilterable))}
        exportCsvButtonProps={{
          buttonLabel: "Export",
          fileNamePrefix: "partner-export",
          tooltip: "Export Partner List"
        }}
        filterMode="server"
        getRowId={(row) => row.mpnId}
        isError={isError}
        loading={loading}
        paginationMode="server"
        paginationOptions={paginationOptions}
        rowCount={rows.count}
        rows={rows}
        setFilterOptions={setFilterOptions}
        setPaginationOptions={setPaginationOptions}
        setSortOptions={setSortOptions}
        slotProps={{
          loadingOverlay: {
            variant: "circular-progress",
            noRowsVariant: "circular-progress"
          }
        }}
        sortingMode="server"
        toolbarConfig={{ disableGridToolbarQuickFilter: true }}
        showExploreDataButtonOnFooter={false}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            pointerEvents: "auto" // re-enables pointer events for column headers to allow sorting and menu interactions
          },
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            userSelect: "none", // prevents text selection on column header click and drag, improving the sorting experience
          },
          "& .MuiDataGrid-columnHeader .MuiSvgIcon-root": {
            pointerEvents: "auto", // re-enables pointer events for icons
            cursor: "pointer" // shows pointer cursor on hover
          },
          "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
            pointerEvents: "auto", // re-enables pointer events for menu icon
            cursor: "pointer" // shows pointer cursor on hover
          }
        }}
      />
    </Box>
  );
}

function navigateTo(partnerOneID: string) {
  return `/partners?partnerOneId=${encodeURIComponent(partnerOneID)}&mpnId=AllUp`;
}

function navigateToMPNPage(partnerOneID: string, mpnId: string) {
  return `/partners?partnerOneId=${encodeURIComponent(partnerOneID)}&mpnId=${encodeURIComponent(mpnId)}`;
}

function getColumn(col: GridColDef, skipColumnsFilterable?: string[]) {
  const stringOperators = getGridStringOperators().filter(
    ({ value }) => !["doesNotContain", "doesNotEqual", "isEmpty", "isNotEmpty", "isAnyOf"].includes(value)
  );

  col.filterable = !(skipColumnsFilterable ?? []).includes(col.field);

  if (
    [
      "cspCustomersCount",
      "cporCustomersCount",
      "palCustomersCount",
      "cspLoginMAU",
      "cporLoginMAU",
      "palLoginMAU"
    ].includes(col.field)
  ) {
    return {
      ...col,
      filterOperators: getGridNumericOperators().filter(
        (operator) => !["isAnyOf", "isNotEmpty", "isEmpty"].includes(operator.value)
      )
    };
  } else if (["mpnId", "mpnName", "partnerOneID", "partnerOneName"].includes(col.field)) {
    return {
      ...col,
      filterOperators: stringOperators
    };
  } else {
    return {
      ...col,
      filterOperators: stringOperators.filter(({ value }) => value === "equals")
    };
  }
}

function copyValue(Id: string, setTitle: Dispatch<SetStateAction<string>>) {
  copy(Id).then(() => {
    setTitle(COPY_TITLE);
  });
}

function onMouseEnterTenantId(setTitle: Dispatch<SetStateAction<string>>) {
  setTitle(COPY_TITLE);
}
